import { useState } from 'react';

import { downloadAccountStatement } from '@api/lendingAPI/LendingAPI';
import { Tab } from '@headlessui/react';
import { IconDownload } from '@iwoca/icons';
import { fetchGetStatements } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { H2 } from '@ui';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import * as selectors from '../AccountHistorySelectors';
import styles from './AccountHistoryTable.module.css';
import { AccountStatementTable } from './accountStatementTable/AccountStatementTable';
import { FundingHistoryTable } from './fundingHistoryTable/FundingHistoryTable';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import ProductType from '../../ui/productType/ProductType';

const mapStateToProps = (state: RootState) => ({
    dateFrom: selectors.getInitialDateFrom(state)
});

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

type TAccountHistoryTableProps = {
    productType: string;
    accountId: string;
    durationInMonths: number;
};

const AccountHistoryTable = ({
    accountId,
    productType,
    durationInMonths,
    dateFrom
}: PropsFromRedux & TAccountHistoryTableProps) => {
    const dateTo = selectors.getInitialDateTo();
    const stateKey = useStateKey();
    const { data } = useQuery({
        queryKey: ['fetchGetStatements', stateKey, dateFrom, dateTo],
        queryFn: () =>
            fetchGetStatements({
                stateKey,
                query: {
                    date_from: dateFrom,
                    date_to: dateTo
                }
            }),
        select: (data) =>
            data.data.find((account) => account.account_id === accountId),
        suspense: true
    });
    const { mutate, isLoading } = useMutation({
        mutationKey: [
            'downloadStatementPDF',
            stateKey,
            accountId,
            dateFrom,
            dateTo
        ],
        mutationFn: () =>
            downloadAccountStatement({
                impersonatingStateKey: stateKey,
                accountId,
                dateFrom,
                dateTo
            })
    });
    const [selectedIndex, setSelectedIndex] = useState(0);

    const downloadAsPDF = () => {
        mutate();
    };

    const accountStatement = data!;

    const hasTransactionHistory = _.some(
        accountStatement?.summary,
        (total) => total !== 0
    );

    if (!hasTransactionHistory || !accountStatement.statements) {
        return (
            <div className={styles.AccountHistoryTable}>
                <div className={styles.header}>
                    <div className={styles.title}>
                        <FormattedMessage
                            id="_.account_history_table.no_transactions"
                            defaultMessage="No transactions yet!"
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="account-history-table">
            <div className="mb-m">
                <H2>
                    <FormattedMessage
                        id="_.account_history_table.title"
                        defaultMessage="{accountType} transaction history"
                        values={{
                            accountType: (
                                <ProductType
                                    productType={productType}
                                    durationInMonths={durationInMonths}
                                />
                            )
                        }}
                    />
                </H2>
            </div>
            <Tab.Group
                selectedIndex={selectedIndex}
                onChange={setSelectedIndex}
            >
                <Tab.List className="mb-m flex flex-col justify-between gap-m min-sm:flex-row">
                    <div>
                        <Tab className="mr-m border-none bg-transparent p-0">
                            {({ selected }) => (
                                <Button
                                    variant={selected ? 'primary' : 'secondary'}
                                >
                                    <FormattedMessage
                                        id="_.account_history_table.account_statement_button"
                                        defaultMessage="Account statement"
                                    />
                                </Button>
                            )}
                        </Tab>
                        <Tab className="border-none bg-transparent p-0">
                            {({ selected }) => (
                                <Button
                                    variant={selected ? 'primary' : 'secondary'}
                                >
                                    <FormattedMessage
                                        id="_.account_history_table.funding_history_button"
                                        defaultMessage="Funding history"
                                    />
                                </Button>
                            )}
                        </Tab>
                    </div>
                    {selectedIndex === 0 && (
                        <Button
                            onClick={downloadAsPDF}
                            loading={isLoading}
                            variant="secondary-alt"
                        >
                            <FormattedMessage
                                id="_.account_history_table.download_button"
                                defaultMessage="Download"
                            />
                            <IconDownload />
                        </Button>
                    )}
                </Tab.List>
                <Tab.Panels>
                    <Tab.Panel>
                        <AccountStatementTable
                            accountStatement={accountStatement}
                        />
                    </Tab.Panel>
                    <Tab.Panel>
                        <FundingHistoryTable
                            fundingHistory={accountStatement.funding_history}
                        />
                    </Tab.Panel>
                </Tab.Panels>
            </Tab.Group>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(AccountHistoryTable);
